import { Lockup } from '../brand/Lockup';
import { BetaNotice } from '../ui/BetaNotice';
import { DISCLAIMER, Footer, Gutter, Page } from '../ui/Chrome';

/**
 * About — what the product is, what its availability is, and who it is not (TROOP-217).
 *
 * **Built rather than deleted, unlike the Privacy and Terms links beside it in the footer.** All
 * three anchors pointed at nothing; this is the one the spec actually asks for, since
 * `handoff/design-system.md` §10 and `DISCLAIMER`'s own doc comment both require the disclaimer to
 * appear in About. Removing the link would have moved the code further from the spec rather than
 * closer, and there is no legal exposure in a paragraph about what the product does. Privacy and
 * Terms took longer because they had to be real documents checked line by line against what the
 * code does — a policy that overstates protection is worse than none — and they exist as of
 * TROOP-203. They wear this page's layout through `LegalDocument`, which is where a fourth public
 * page's chrome should come from too; this one is older than that component and is deliberately
 * left alone rather than retrofitted, since its body is prose in its own shape rather than a
 * document.
 *
 * **Public, and reachable signed in or out.** It is linked from every page footer, so it cannot be
 * gated on a session; and it is the page a committee chair evaluating the product actually reads,
 * which is why the beta note carries a fuller framing here than on the signed-out page.
 *
 * **The disclaimer appears twice on this page and that is deliberate.** §10 enumerates three
 * places — every page footer, the sign-up screen, and About — and this page has a footer as well
 * as being About. Rendering it once in the body, at body size where somebody reads it, and once in
 * the footer where every other page carries it, keeps both halves of that rule true. It is fixed
 * text, never abbreviated, never behind a disclosure control. **The beta card sits between them on
 * purpose**: written in document order the two landed adjacent, and the same sentence twice in
 * three hundred pixels reads as a duplication bug rather than as a statement made twice.
 *
 * **"Troops and packs", not "troops, packs and crews".** `UnitType` is `Troop` and `Pack`, and a
 * unit's whole vocabulary is derived from it — a Crew onboarded today would be labelled a Troop
 * and told about its patrols. `HomeScreen`'s eyebrow says crews and is older than this; the ticket
 * that added this page asks for accurate rather than aspirational, so the two disagree on purpose
 * until a `Crew` unit type exists. Do not "correct" this one to match.
 *
 * **This page is signed-out too, so the domain's vocabulary stays off it** (TROOP-228). The
 * opening paragraph said events and announcements are "objects that hold the facts" — the same
 * sentence `HomeScreen` led with, and a reader who follows *Ask for an invite* to `BetaNotice` and
 * on to About meets it one click after being spared it. The framing here is fuller on purpose
 * (a committee chair evaluating the product reads this one); fuller is not the same as more
 * technical.
 *
 * **No masthead.** `SignInScreen` is the precedent for a standalone public page: the lockup sits at
 * the top of the content rather than in a bar of its own. A second inline masthead beside
 * `HomeScreen`'s would be two of them to keep in step. The lockup is the way back to the front
 * page.
 */
export function AboutScreen() {
  return (
    <Page>
      <Gutter>
        <div style={{ paddingBlock: 'var(--space-7)', maxWidth: '46rem' }}>
          {/* No negative margin of its own: `Lockup` already pulls its own optical inset back out
              so the mark's lower rect lands on the gutter. A second correction here — copied from
              `AppBar`, where it cancels the back button's tap padding — put the mark 8px left of
              every paragraph under it. */}
          <a href="/" aria-label="TroopHerald home" style={{ display: 'inline-block' }}>
            <Lockup size={30} />
          </a>

          <h1
            className="t-display"
            style={{
              color: 'var(--color-text-primary)',
              margin: 'var(--space-6) 0 var(--space-4)',
              textWrap: 'balance',
            }}
          >
            About TroopHerald
          </h1>

          <p
            className="t-body"
            style={{ color: 'var(--color-text-secondary)', margin: 0, maxWidth: '54ch' }}
          >
            TroopHerald is a communication tool for troops and packs. It replaces the group
            chat where &ldquo;what time do we leave Friday&rdquo; ends up under two hundred messages
            about who is bringing the griddle: the details of a trip live on the trip itself, and
            the conversation about it sits underneath.
          </p>

          <p
            className="t-body"
            style={{
              color: 'var(--color-text-secondary)',
              margin: 'var(--space-4) 0 0',
              maxWidth: '54ch',
            }}
          >
            It is built around rules a unit already works to, enforced in the product rather than
            left to whoever is paying attention. A thread with young people in it cannot drop below
            two adults. A young person&rsquo;s contact details belong to their guardians. And a
            correction is made once, on the thing it is about, rather than in fifty phones.
          </p>

          <section style={{ marginTop: 'var(--space-7)' }}>
            <h2
              className="t-heading"
              style={{ color: 'var(--color-text-primary)', margin: 0, textWrap: 'balance' }}
            >
              Independent
            </h2>

            <p
              className="t-body"
              style={{
                color: 'var(--color-text-primary)',
                margin: 'var(--space-3) 0 0',
                maxWidth: '54ch',
              }}
            >
              {DISCLAIMER}
            </p>
          </section>

          {/* Last, because it is the one thing on this page anybody is asked to act on — and
              because it is what stands between the two renderings of the disclaimer. */}
          <div style={{ marginTop: 'var(--space-7)' }}>
            <BetaNotice />
          </div>
        </div>
      </Gutter>

      <Footer />
    </Page>
  );
}
